/**
 * Pritect Beacon, consent script entry point.
 *
 * Customers never hand-write this tag. The dashboard's Deploy tab generates it
 * from the published release manifest. The shape it emits:
 *
 * <script
 *   src="https://ynbeopknlxoayzdocsmu.supabase.co/storage/v1/object/public/consent-scripts/v1/pritect-beacon.min.js"
 *   data-domain-id="YOUR_DOMAIN_ID"
 * ></script>
 *
 * data-api-url and data-anon-key are optional overrides. Without them the
 * engine talks to the instance the bundle was built for (see buildConfig.ts).
 *
 * That stable URL serves the LOADER (src/loader/loader-entry.ts), not this
 * bundle. The loader starts blocking scripts synchronously and then injects
 * this file pinned by content hash, which is how a release reaches live sites
 * with no snippet to re-copy. This bundle also runs standalone, with no loader,
 * for version-pinned embeds; see the window.__pbLoader handoff in boot() below.
 *
 * The bucket has two channels. releases/pritect-beacon.<hash>.min.js is
 * immutable and MUST carry an integrity attribute. v1/pritect-beacon.min.js is
 * rewritten by every publish and MUST NOT: the next deploy would break the hash
 * and the browser would silently refuse to run the script, which is exactly
 * what took consent management off pritect.ai on 9 Aug 2026. See
 * scripts/check-sri-pinning.mjs.
 */

import { ConsentEngine } from "./core/ConsentEngine";
import { GoogleConsentMode, repairQueuedGtagCommands } from "./gcm/GoogleConsentMode";
import { getScriptConfig, installTcfStub } from "./scriptConfig";
import { DEFAULT_ANON_KEY, DEFAULT_API_URL } from "./buildConfig";
import { installPreferenceLinks } from "./ui/preferenceLinks";
import { findEmbedScript, watchHostLayout } from "./ui/hostLayout";

let engine: ConsentEngine | null = null;
let pendingPreferencesOpen = false;

async function boot(): Promise<void> {
  // When the stable loader injected this bundle it has already parsed the embed
  // config, installed the GCM defaults and the TCF stub, and been intercepting
  // scripts since before any other tag on the page ran. Redoing any of that
  // here would overwrite live state, so its work is adopted instead. Absent for
  // version-pinned embeds, which load this bundle directly.
  const handoff = window.__pbLoader;

  const config = handoff?.config ?? getScriptConfig();
  if (!config) return;

  // Before any await, while document.currentScript still names a directly
  // embedded tag. Read here, not from the handoff, so bootstraps already
  // published (and pinned by SRI on customer sites) need no change for it.
  try { watchHostLayout(findEmbedScript(config.domainId)); } catch { /* the published layout applies */ }

  // Before any await, so a click on the site's own "Cookie settings" link
  // during boot is queued by showPreferences rather than lost.
  try { installPreferenceLinks(() => window.PritectBeacon.showPreferences()); } catch { /* no-op */ }

  // An older bootstrap may have queued Consent Mode commands in a form Google
  // ignores. Fixed here, in the core, so those sites need no new snippet.
  try { repairQueuedGtagCommands(); } catch { /* no-op */ }

  // Synchronous consent-signalling setup, before any await, so it lands before
  // gtag / TCF vendor tags execute. Opt-in via data-gcm / data-tcf on the embed.
  let gcmDefaultsInstalled = handoff?.gcmInstalled === true;
  if (config.gcm && !gcmDefaultsInstalled) {
    try {
      new GoogleConsentMode(true).setDefaults();
      gcmDefaultsInstalled = true;
    } catch { /* no-op */ }
  }
  if (config.tcf && !handoff?.tcfInstalled) {
    try { installTcfStub(); } catch { /* no-op */ }
  }

  engine = new ConsentEngine({
    domainId: config.domainId,
    apiUrl: (config.apiUrl ?? DEFAULT_API_URL).replace(/\/$/, ""),
    anonKey: config.anonKey ?? DEFAULT_ANON_KEY,
    lang: config.lang,
    blocklist: config.blocklist,
    blocker: handoff?.blocker,
    gcmDefaultsInstalled,
    tagManagerAllowed: gcmDefaultsInstalled && config.gcmAdvanced === true,
  });

  await engine.init();
  if (pendingPreferencesOpen || handoff?.pendingPreferences) {
    pendingPreferencesOpen = false;
    if (handoff) handoff.pendingPreferences = false;
    engine.showPreferences();
  }
}

// Expose public API on window for custom integrations
declare global {
  interface Window {
    PritectBeacon: {
      showPreferences: () => void;
      revokeConsent: () => void;
    };
  }
}

let booted: Promise<void> = Promise.resolve();

window.PritectBeacon = {
  showPreferences: () => {
    if (engine) {
      engine.showPreferences();
      return;
    }
    pendingPreferencesOpen = true;
  },
  // Withdrawal must be as effective as giving consent (GDPR Art. 7(3)), so it
  // goes through the engine: a refusal is recorded, the tracker cookies of
  // every category are cleared, and the page reloads once the record settles.
  revokeConsent: () => {
    void booted
      .catch(() => { /* reported below */ })
      .then(() => {
        if (engine) return engine.revokeConsent();
        // No engine (the embed has no data-domain-id): nothing was ever
        // recorded, so only the local state and tracker cookies can go.
        ConsentEngine.clearLocalState(null);
        window.location.reload();
      });
  },
};

// Boot immediately so script interception starts before later tags execute.
booted = boot();
void booted.catch((error) => {
  console.warn("[Pritect Beacon] Failed to initialize.", error);
  window.dispatchEvent(new CustomEvent("pb:error", { detail: { message: "Failed to initialize consent manager." } }));
});
